import { ReactElement } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { RequiredAuth } from "routes/required-auth"
import { SigninOIDC } from "scenes/authentications/signin-oidc";
import { Logout } from "scenes/authentications/logout";
import { LogoutCallback } from "scenes/authentications/logout-callback";
import { SilentRenew } from "scenes/authentications/silent-renew";
import ProfileScene from "scenes/accounts/profile";
import ProfileEditScene from "scenes/accounts/profile/edit";
import ScrollToTop from "components/scroll-to-top";
import DashboardScene from "scenes/dashboard";
import OrderScene from "scenes/orders";
import OrderDetailScene from "scenes/orders/order-detail";
import PartnerRequestPaymentScene from "scenes/partners/request-payment";
import RequestWithdrawDetailScene from "scenes/partners/request-payment/payment-detail";
import ListProgramScene from "scenes/programs/list";
import CreateNewProgramScene from "scenes/programs/create";
import ProgramDetailScene from "scenes/programs/detail";
import ProgramEditScene from "scenes/programs/edit";
import MemberScene from "scenes/members";
import MemberProfileScene from "scenes/members/detail";
import ListPartnerScene from "scenes/partners/list";
import PartnerProfileScene from "scenes/partners/detail";
import StoreOrderScene from "scenes/store-order";
import StoreOrderDetailScene from "scenes/store-order/detail";
import ProductListScene from "scenes/products/list";
import VoucherScene from "scenes/vouchers";
import EditVoucher from "scenes/vouchers/edit";
import NewVoucher from "scenes/vouchers/new";
import ShortenScene from "scenes/shorten";
import NewShortUrl from "scenes/shorten/new";
import ShortenDetail from "scenes/shorten/details";
import EditShortUrl from "scenes/shorten/edit";
import ProgramLesson from "scenes/programs/lessons";
import NewProgramLessonScene from "scenes/programs/lessons/new";
import EditProgramLessonScene from "../scenes/programs/lessons/edit";
import { RequiredAdminAuth } from "./required-admin-auth";
import BannerScene from "../scenes/banner";
import NewBannerScene from "../scenes/banner/new";
import BannerDetailScene from "../scenes/banner/details";
import EditBannerScene from "../scenes/banner/edit";
import AuthorScene from "../scenes/authors";
import NewAuthorScene from "../scenes/authors/new";
import AuthorDetailScene from "../scenes/authors/details";
import EditAuthorScene from "../scenes/authors/edit";

const Router = (): ReactElement => (
    <BrowserRouter>
        <ScrollToTop>
            <Routes>
                <Route path='/signin-oidc' element={<SigninOIDC />} />
                <Route path='/logout' element={<Logout />} />
                <Route path='/logout/callback' element={<LogoutCallback />} />
                <Route path='/silentrenew' element={<SilentRenew />} />

                <Route path='/profile' element={<RequiredAuth><ProfileScene /></RequiredAuth>} />
                <Route path='/profile/edit' element={<RequiredAuth><ProfileEditScene /></RequiredAuth>} />

                <Route path='/dashboard' element={<RequiredAdminAuth><DashboardScene /></RequiredAdminAuth>} />
                <Route path='/orders' element={<RequiredAdminAuth><OrderScene /></RequiredAdminAuth>} />
                <Route path='/orders/:orderId' element={<RequiredAdminAuth><OrderDetailScene /></RequiredAdminAuth>} />

                <Route path='/storeorders' element={<RequiredAdminAuth><StoreOrderScene /></RequiredAdminAuth>} />
                <Route path='/storeorders/:orderId' element={<RequiredAdminAuth><StoreOrderDetailScene /></RequiredAdminAuth>} />

                <Route path='/members' element={<RequiredAdminAuth><MemberScene /></RequiredAdminAuth>} />
                <Route path='/members/:memberId' element={<RequiredAdminAuth><MemberProfileScene /></RequiredAdminAuth>} />

                <Route path='/programs' element={<RequiredAuth><ListProgramScene /></RequiredAuth>} />
                <Route path='/programs/new' element={<RequiredAuth><CreateNewProgramScene /></RequiredAuth>} />
                <Route path='/programs/:programId' element={<RequiredAuth><ProgramDetailScene /></RequiredAuth>} />
                <Route path='/programs/:programId/edit' element={<RequiredAuth><ProgramEditScene /></RequiredAuth>} />
                <Route path='/programs/:programId/lessongroups/:lessonGroupId/new' element={<RequiredAuth><NewProgramLessonScene /></RequiredAuth>} />
                <Route path='/programs/:programId/lessongroups/:lessonGroupId/lessons/:lessonId' element={<RequiredAuth><ProgramLesson /></RequiredAuth>} />
                <Route path='/programs/:programId/lessongroups/:lessonGroupId/lessons/:lessonId/edit' element={<RequiredAuth><EditProgramLessonScene /></RequiredAuth>} />

                <Route path='/products' element={<RequiredAuth><ProductListScene /></RequiredAuth>} />

                <Route path='/partners' element={<RequiredAdminAuth><ListPartnerScene /></RequiredAdminAuth>} />
                <Route path='/partners/:partnerId' element={<RequiredAdminAuth><PartnerProfileScene /></RequiredAdminAuth>} />
                <Route path='/partners/request' element={<RequiredAdminAuth><PartnerRequestPaymentScene /></RequiredAdminAuth>} />
                <Route path='/partners/request/:withdrawId' element={<RequiredAdminAuth><RequestWithdrawDetailScene /></RequiredAdminAuth>} />

                <Route path='/vouchers' element={<RequiredAuth><VoucherScene /></RequiredAuth>} />
                <Route path='/vouchers/new' element={<RequiredAuth><NewVoucher /></RequiredAuth>} />
                <Route path='/vouchers/:voucherId' element={<RequiredAuth><EditVoucher /></RequiredAuth>} />

                <Route path='/shorturls' element={<RequiredAuth><ShortenScene /></RequiredAuth>} />
                <Route path='/shorturls/new' element={<RequiredAuth><NewShortUrl /></RequiredAuth>} />
                <Route path='/shorturls/:shortLinkId' element={<RequiredAuth><ShortenDetail /></RequiredAuth>} />
                <Route path='/shorturls/:shortLinkId/edit' element={<RequiredAuth><EditShortUrl /></RequiredAuth>} />

                <Route path='/banners' element={<RequiredAuth><BannerScene /></RequiredAuth>} />
                <Route path='/banners/new' element={<RequiredAuth><NewBannerScene /></RequiredAuth>} />
                <Route path='/banners/:bannerId' element={<RequiredAuth><BannerDetailScene /></RequiredAuth>} />
                <Route path='/banners/:bannerId/edit' element={<RequiredAuth><EditBannerScene /></RequiredAuth>} />

                <Route path='/authors' element={<RequiredAuth><AuthorScene /></RequiredAuth>} />
                <Route path='/authors/new' element={<RequiredAuth><NewAuthorScene /></RequiredAuth>} />
                <Route path='/authors/:authorId' element={<RequiredAuth><AuthorDetailScene /></RequiredAuth>} />
                <Route path='/authors/:authorId/edit' element={<RequiredAuth><EditAuthorScene /></RequiredAuth>} />

                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </ScrollToTop>
    </BrowserRouter>
);

export default Router;